import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import MapCanvas from "../../../GlobalMap/map/MapCanvas"
import type { FitBounds } from "../../../GlobalMap/map/fit"
import type {
  FallbackCenter,
  MapFeature,
  MapFeaturesResponse
} from "../../../GlobalMap/redux/global-map.types"

interface HomeLocationsMapProps {
  features: MapFeature[]
  response: MapFeaturesResponse | null
  fitTo: FallbackCenter | FitBounds | null
  selectedId: string | null
  onSelect: (id: string) => void
}

/**
 * The Global Map's own canvas, on Home.
 *
 * `MapCanvas` is entirely prop-driven — it takes the response, the fit and
 * the selection and reports back — so Home can mount the real map, with the
 * real markers (`markers.ts` draws the green house and blue pin from
 * `entity-icon-colors`) and the real clustering, without borrowing the Global
 * Map page's redux state. Nothing about the marker vocabulary is re-drawn
 * here, which is what keeps Home's legend and the map page's identical.
 */
export const HomeLocationsMap = ({
  features,
  response,
  fitTo,
  selectedId,
  onSelect
}: HomeLocationsMapProps) => {
  const { t } = useTranslation()
  const reducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const handleSelect = useCallback((id: string) => onSelect(id), [onSelect])

  return (
    <div style={{ height: "100%", minHeight: 200 }}>
      <MapCanvas
        // Clustering is the server's own call: it answers `mode: "clusters"`
        // when the result is too dense to pin individually, and the canvas
        // already knows how to draw either.
        data={
          Array.isArray(response?.features)
            ? {
                mode: response.mode ?? "features",
                features,
                clusters: response.clusters ?? []
              }
            : null
        }
        fitTo={fitTo}
        selectedId={selectedId}
        reducedMotion={reducedMotion}
        nearCenter={null}
        nearRadiusMi={null}
        onSelectFeature={handleSelect}
        onClusterClick={() => {
          // Home's map is an overview, not a drill surface: expanding a
          // cluster belongs on the Locations page, which "Open Locations"
          // reaches. Deliberately inert rather than half-wired.
        }}
        onViewportChange={() => {
          // Home never refetches on pan — the section always shows the whole
          // authorized portfolio, so a viewport change changes nothing.
        }}
        onError={() => {
          // `MapCanvas` renders its own provider-error state in place.
        }}
        onLocateMe={() => {}}
        locateMeLabel={t("globalMap.map.locateMe", "Center on my location")}
        fullscreenLabel={t("globalMap.map.fullscreen", "Full screen")}
        changeMapStyleLabel={t("globalMap.map.changeStyle", "Change map style")}
        mapTypeLabel={t("globalMap.map.mapType", "Map")}
        satelliteTypeLabel={t("globalMap.map.satelliteType", "Satellite")}
      />
    </div>
  )
}
